import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import SmsIcon from '@mui/icons-material/Sms'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import {
  receiverOptOutService,
  ReceiverSmsOptOutDto,
} from '../api/adminServices'
import { useToast } from '../components/useToast'

const PAGE_SIZE = 20

export default function ReceiverOptOutsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [confirmTarget, setConfirmTarget] = useState<ReceiverSmsOptOutDto | null>(null)

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['admin-receiver-opt-outs', page],
    queryFn: () => receiverOptOutService.list(page, PAGE_SIZE),
  })

  const rows: ReceiverSmsOptOutDto[] = useMemo(() => data?.content ?? [], [data])
  const totalPages: number = data?.totalPages ?? 0
  const totalElements: number = data?.totalElements ?? 0

  const removeMutation = useMutation({
    mutationFn: (optOutId: string) => receiverOptOutService.remove(optOutId),
    onSuccess: () => {
      toast.success('Opt-out removed. This receiver will get confirmation SMS again.')
      setConfirmTarget(null)
      queryClient.invalidateQueries({ queryKey: ['admin-receiver-opt-outs'] })
    },
    onError: (e) => toast.errorFrom(e, 'Could not remove opt-out'),
  })

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <SmsIcon color="primary" />
              <Typography variant="h6">Receiver SMS Opt-outs</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Receivers who unsubscribed from cargo readiness-confirmation SMS. Remove an
              opt-out to re-enable messages for that number.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load receiver opt-outs. Please try again.
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No receivers have opted out.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Phone (masked)</TableCell>
                  <TableCell>Opted out at</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.maskedPhone}</TableCell>
                    <TableCell>
                      {row.optedOutAt ? new Date(row.optedOutAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.source || 'UNKNOWN'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<NotificationsActiveIcon />}
                        onClick={() => setConfirmTarget(row)}
                      >
                        Re-enable SMS
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <Typography variant="caption" color="text.secondary">
              {totalElements} opted-out number(s)
            </Typography>
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_e, v) => setPage(v - 1)}
                color="primary"
              />
            )}
          </Stack>
        </>
      )}

      <Dialog open={!!confirmTarget} onClose={() => setConfirmTarget(null)}>
        <DialogTitle>Re-enable confirmation SMS?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The number <strong>{confirmTarget?.maskedPhone}</strong> will start receiving cargo
            readiness-confirmation SMS again. Only do this if the receiver has agreed to receive
            these messages.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)} disabled={removeMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => confirmTarget && removeMutation.mutate(confirmTarget.id)}
            disabled={removeMutation.isPending}
          >
            {removeMutation.isPending ? 'Removing…' : 'Re-enable'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
